import { useReducedMotion } from '@mantine/hooks';
import * as React from 'react';

import { type DocumentSearchSortBy } from '@/utils/sortDocumentSearchItems';

type UseSearchSortTransitionResult = {
  reduceMotion: boolean;
  displayedSortBy: DocumentSearchSortBy;
  visible: boolean;
  handleTransitionEnd: (event: React.TransitionEvent<HTMLDivElement>) => void;
};

export const useSearchSortTransition = (sortBy: DocumentSearchSortBy): UseSearchSortTransitionResult => {
  const reduceMotion = Boolean(useReducedMotion());
  const [displayedSortBy, setDisplayedSortBy] = React.useState(sortBy);
  const [visible, setVisible] = React.useState(true);

  React.useLayoutEffect(() => {
    if (reduceMotion) {
      setDisplayedSortBy(sortBy);
      setVisible(true);

      return;
    }

    if (sortBy === displayedSortBy) {
      setVisible(true);

      return;
    }

    setVisible(false);
  }, [sortBy, displayedSortBy, reduceMotion]);

  const handleTransitionEnd = React.useCallback(
    (event: React.TransitionEvent<HTMLDivElement>) => {
      if (reduceMotion) {
        return;
      }

      if (event.target !== event.currentTarget || event.propertyName !== 'opacity') {
        return;
      }

      if (!visible && sortBy !== displayedSortBy) {
        setDisplayedSortBy(sortBy);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setVisible(true);
          });
        });
      }
    },
    [reduceMotion, visible, sortBy, displayedSortBy],
  );

  return { reduceMotion, displayedSortBy, visible, handleTransitionEnd };
};
